import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import eatingWayService from 'features/EatingWay/eatingWayService';
import { EatingWayAreaImage } from 'features/EatingWay/models';
import { EatingWay } from 'features/EatingWay/models/EatingWay';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { MenuBookProperty, SaleType, SaleTypeMap } from 'features/common/models';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EATING_WAY_AREA_IMAGE_TYPE, EATING_WAY_BACKGROUND_SETTING, displayError, handleServiceError } from 'utils';
import { EatingWayAreaImageModal, EatingWayAreaImageModalProps } from '../EatingWayAreaImageModal';
import { SelectBackgroundModal, SelectBackgroundModalProps } from '../SelectBackgroundModal';
import './style.scss';

// const DEFAULT_TEXT_COLOR = '#fff';
const SelectBackgroundModalType = {
  main_genre: 1,
  main_subgenre: 2,
  other_genre: 3,
  other_subgenre: 4,
};

export interface EatingWayDetailModalProps {
  eatingWayId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

export interface SelectBackgroundModalData extends SelectBackgroundModalProps {
  type: number;
}

export function EatingWayDetailModal({ eatingWayId, onClose }: EatingWayDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // refs
  const colorMainGenreInputRef = useRef<HTMLInputElement>(null);
  const colorMainSubGenreInputRef = useRef<HTMLInputElement>(null);
  const colorOtherGenreInputRef = useRef<HTMLInputElement>(null);
  const colorOtherSubGenreInputRef = useRef<HTMLInputElement>(null);

  // state
  const [eatingWayData, setEatingWayData] = useState<EatingWay>();
  const [saleTypeSourceMap, setSaleTypeSourceMap] = useState<SaleTypeMap>();
  const [propertySource, setPropertySource] = useState<{ [key: number]: string }>();
  const [selectBackgroundModalData, setSelectBackgroundModalData] = useState<SelectBackgroundModalData>();
  const [showSelectBackgroundModal, setShowSelectBackgroundModal] = useState(false);
  const [eatingWayAreaImageModalData, setEatingWayAreaImageModalData] = useState<EatingWayAreaImageModalProps>();
  const [showEatingWayAreaImageModal, setShowEatingWayAreaImageModal] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useForm<Partial<EatingWay>>({
    defaultValues: {
      name: '',
      main_genre_bg_color1: EATING_WAY_BACKGROUND_SETTING.main.genre.color1,
      main_genre_bg_color2: EATING_WAY_BACKGROUND_SETTING.main.genre.color2,
      main_genre_text_color: EATING_WAY_BACKGROUND_SETTING.textColor,

      main_subgenre_bg_color1: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color1,
      main_subgenre_bg_color2: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color2,
      main_subgenre_text_color: EATING_WAY_BACKGROUND_SETTING.textColor,

      other_genre_bg_color1: EATING_WAY_BACKGROUND_SETTING.other.genre.color1,
      other_genre_bg_color2: EATING_WAY_BACKGROUND_SETTING.other.genre.color2,
      other_genre_text_color: EATING_WAY_BACKGROUND_SETTING.textColor,

      other_subgenre_bg_color1: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color1,
      other_subgenre_bg_color2: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color2,
      other_subgenre_text_color: EATING_WAY_BACKGROUND_SETTING.textColor,
      note: '',
      eating_way_area_images: [],
    },
  });

  // watch
  const watchEatingWayName = useWatch({ control, name: 'name', defaultValue: '' });
  const watchProperty = useWatch({ control, name: 'property' });
  // main genre
  const watchMainGenreBgColor1 = useWatch({
    control,
    name: 'main_genre_bg_color1',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.main.genre.color1,
  });
  const watchMainGenreBgColor2 = useWatch({
    control,
    name: 'main_genre_bg_color2',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.main.genre.color2,
  });
  const watchMainGenreTextColor = useWatch({
    control,
    name: 'main_genre_text_color',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.textColor,
  });

  // main subgenre
  const watchMainSubGenreBgColor1 = useWatch({
    control,
    name: 'main_subgenre_bg_color1',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color1,
  });
  const watchMainSubGenreBgColor2 = useWatch({
    control,
    name: 'main_subgenre_bg_color2',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color2,
  });
  const watchMainSubGenreTextColor = useWatch({
    control,
    name: 'main_subgenre_text_color',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.textColor,
  });

  // other genre
  const watchOtherGenreBgColor1 = useWatch({
    control,
    name: 'other_genre_bg_color1',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.other.genre.color1,
  });
  const watchOtherGenreBgColor2 = useWatch({
    control,
    name: 'other_genre_bg_color2',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.other.genre.color2,
  });
  const watchOtherGenreTextColor = useWatch({
    control,
    name: 'other_genre_text_color',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.textColor,
  });

  // other subgenre
  const watchOtherSubGenreBgColor1 = useWatch({
    control,
    name: 'other_subgenre_bg_color1',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color1,
  });
  const watchOtherSubGenreBgColor2 = useWatch({
    control,
    name: 'other_subgenre_bg_color2',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color2,
  });
  const watchOtherSubGenreTextColor = useWatch({
    control,
    name: 'other_subgenre_text_color',
    defaultValue: EATING_WAY_BACKGROUND_SETTING.textColor,
  });

  function loadSaleTypeDataSource() {
    (async () => {
      try {
        const response = await masterDataService.getSaleType();
        const saleTypeMap = response.data.sale_type.reduce((saleTypeMap: SaleTypeMap, saleType: SaleType) => {
          saleTypeMap[saleType.id] = saleType.name;
          return saleTypeMap;
        }, {});
        setSaleTypeSourceMap(saleTypeMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadPropertyDataSource() {
    //load data for property source
    (async () => {
      try {
        const response = await masterDataService.getMenuBookProperty();
        const propertyMap = response.data.menu_book_property.reduce(
          (menuBookPropertyMap: { [key: number]: string }, menuBookProperty: MenuBookProperty) => {
            menuBookPropertyMap[menuBookProperty.id] = menuBookProperty.name;
            return menuBookPropertyMap;
          },
          {}
        );
        setPropertySource(propertyMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadEatingWayData() {
    try {
      if (eatingWayId) {
        (async () => {
          const response = await eatingWayService.getItem(eatingWayId);
          setEatingWayData(response.data);
          reset(response.data);
        })();
      }
    } catch (e) {}
  }

  useEffect(() => {
    loadSaleTypeDataSource();
    loadPropertyDataSource();
    loadEatingWayData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose(isDataChanged: boolean = false) {
    onClose && onClose(isDataChanged);
  }

  function handleColorMainGenreClick() {
    if (colorMainGenreInputRef.current) {
      colorMainGenreInputRef.current.click();
    }
  }

  function handleColorMainSubGenreClick() {
    if (colorMainSubGenreInputRef.current) {
      colorMainSubGenreInputRef.current.click();
    }
  }

  function handleColorOtherGenreClick() {
    if (colorOtherGenreInputRef.current) {
      colorOtherGenreInputRef.current.click();
    }
  }

  function handleColorOtherSubGenreClick() {
    if (colorOtherSubGenreInputRef.current) {
      colorOtherSubGenreInputRef.current.click();
    }
  }

  // SelectBackgroundModal events
  // main genre
  function handleOpenSelectBackgroundModal(type: number) {
    let data: SelectBackgroundModalData | null = null;
    if (type === SelectBackgroundModalType.main_genre) {
      data = {
        type: type,
        title: t('SelectBackgroundModal.main_menu_book'),
        subtitle: t('SelectBackgroundModal.genre'),
        bgcolor1: watchMainGenreBgColor1,
        bgcolor2: watchMainGenreBgColor2,
        defaultBgColor1: EATING_WAY_BACKGROUND_SETTING.main.genre.color1,
        defaultBgColor2: EATING_WAY_BACKGROUND_SETTING.main.genre.color2,
      };
    } else if (type === SelectBackgroundModalType.main_subgenre) {
      data = {
        type: type,
        title: t('SelectBackgroundModal.main_menu_book'),
        subtitle: t('SelectBackgroundModal.subgenre'),
        bgcolor1: watchMainSubGenreBgColor1,
        bgcolor2: watchMainSubGenreBgColor2,
        defaultBgColor1: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color1,
        defaultBgColor2: EATING_WAY_BACKGROUND_SETTING.main.subgenre.color2,
      };
    } else if (type === SelectBackgroundModalType.other_genre) {
      data = {
        type: type,
        title: t('SelectBackgroundModal.other_menu_book'),
        subtitle: t('SelectBackgroundModal.genre'),
        bgcolor1: watchOtherGenreBgColor1,
        bgcolor2: watchOtherGenreBgColor2,
        defaultBgColor1: EATING_WAY_BACKGROUND_SETTING.other.genre.color1,
        defaultBgColor2: EATING_WAY_BACKGROUND_SETTING.other.genre.color2,
      };
    } else if (type === SelectBackgroundModalType.other_subgenre) {
      data = {
        type: type,
        title: t('SelectBackgroundModal.other_menu_book'),
        subtitle: t('SelectBackgroundModal.subgenre'),
        bgcolor1: watchOtherSubGenreBgColor1,
        bgcolor2: watchOtherSubGenreBgColor2,
        defaultBgColor1: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color1,
        defaultBgColor2: EATING_WAY_BACKGROUND_SETTING.other.subgenre.color2,
      };
    }
    if (data) {
      setSelectBackgroundModalData(data);
      setShowSelectBackgroundModal(true);
    }
  }

  function handleCloseSelectBackgroundModal() {
    setShowSelectBackgroundModal(false);
  }

  function handleReflectSelectBackgroundModal(bgColor1: string, bgColor2: string) {
    if (selectBackgroundModalData) {
      switch (selectBackgroundModalData.type) {
        case SelectBackgroundModalType.main_genre:
          setValue('main_genre_bg_color1', bgColor1);
          setValue('main_genre_bg_color2', bgColor2);
          break;
        case SelectBackgroundModalType.main_subgenre:
          setValue('main_subgenre_bg_color1', bgColor1);
          setValue('main_subgenre_bg_color2', bgColor2);
          break;
        case SelectBackgroundModalType.other_genre:
          setValue('other_genre_bg_color1', bgColor1);
          setValue('other_genre_bg_color2', bgColor2);
          break;
        case SelectBackgroundModalType.other_subgenre:
          setValue('other_subgenre_bg_color1', bgColor1);
          setValue('other_subgenre_bg_color2', bgColor2);
          break;
      }
    }
  }
  // end SelectBackgroundModal events

  // EatingWayAreaImageModal events
  // area 1
  function handleOpenEatingWayAreaImageModal(areaType: number) {
    const eatingWayAreaImages = getValues('eating_way_area_images') || [];
    const eatingWayAreaImage = eatingWayAreaImages.find((areaImage) => areaImage.area_type === areaType);

    let data: EatingWayAreaImageModalProps = {
      eatingWayAreaImage: eatingWayAreaImage,
      eatingWayName: watchEatingWayName,
      propertyName: propertySource?.[watchProperty || 0] ?? '',
      areaType: areaType,
    };
    setEatingWayAreaImageModalData(data);
    setShowEatingWayAreaImageModal(true);
  }

  function handleCloseEatingWayAreaImageModal() {
    setShowEatingWayAreaImageModal(false);
  }

  function handleReflectEatingWayAreaImageModal(returnData: Partial<EatingWayAreaImage>) {
    const eatingWayAreaImages = getValues('eating_way_area_images') || [];
    const index = eatingWayAreaImages.findIndex((areaImage) => areaImage.area_type === returnData.area_type);
    if (index >= 0) {
      eatingWayAreaImages[index] = returnData;
    } else {
      eatingWayAreaImages.push(returnData);
    }

    setValue('eating_way_area_images', eatingWayAreaImages);
  }

  // end EatingWayAreaImageModal events

  function onSubmit(formValue: Partial<EatingWay>) {
    if (eatingWayId) {
      // case update
      (async () => {
        try {
          const response = await eatingWayService.update(eatingWayId, formValue);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } else {
      // case add new
      (async () => {
        try {
          const response = await eatingWayService.add(formValue);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  // compose isLoading status
  let isLoading = true;
  if (eatingWayId) {
    if (propertySource && saleTypeSourceMap && eatingWayData) {
      isLoading = false;
    }
  } else {
    if (propertySource && saleTypeSourceMap) {
      isLoading = false;
    }
  }

  if (isLoading) {
    return null;
  }

  return (
    <Modal size="lg" dialogClassName="eating-way-detail-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('EatingWayDetailModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col sm={9} className="small">
              <div>{t('EatingWayDetailModal.guide_text1')}</div>
              <div className="ps-3">{t('EatingWayDetailModal.guide_text2')}</div>
              <div>{t('EatingWayDetailModal.guide_text3')}</div>
              <div className="ps-2">{t('EatingWayDetailModal.guide_text4')}</div>
              <div>{t('EatingWayDetailModal.guide_text5')}</div>
            </Col>
            <Col sm={3} className="text-end">
              <Button type="submit" className="btn--dark-green" disabled={isSubmitting}>
                {t('EatingWayDetailModal.save')}
              </Button>
            </Col>
          </Row>
          <Table bordered className="form-menu-book">
            <tbody>
              <tr>
                <th colSpan={2} style={{ width: '40%' }}>
                  {t('EatingWayDetailModal.sale_type_name')}
                </th>
                <td style={{ width: '60%' }} className="text-center">
                  {Boolean(eatingWayId) && eatingWayData && saleTypeSourceMap && (
                    <span>{saleTypeSourceMap[eatingWayData.sale_type_id]}</span>
                  )}
                  {!Boolean(eatingWayId) && (
                    <Form.Select className="text-center" {...register('sale_type_id')}>
                      {saleTypeSourceMap &&
                        Object.keys(saleTypeSourceMap).map((key) => (
                          <option key={key} value={key}>
                            {saleTypeSourceMap[Number(key)]}
                          </option>
                        ))}
                    </Form.Select>
                  )}
                </td>
              </tr>
              <tr>
                <th colSpan={2}>{t('EatingWayDetailModal.eating_way_name')}</th>
                <td>
                  <Form.Control className="text-center" {...register('name')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th colSpan={2}>{t('EatingWayDetailModal.property')}</th>
                <td>
                  {propertySource && (
                    <Form.Select className="text-center" {...register('property')}>
                      {Object.keys(propertySource).map(function (key) {
                        return (
                          <option key={key} value={key}>
                            {propertySource[Number(key)]}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                </td>
              </tr>
              <tr>
                <th
                  colSpan={2}
                  dangerouslySetInnerHTML={{ __html: t('EatingWayDetailModal.head_title_setting_color') }}
                ></th>
                <td>
                  <div className="mb-4">
                    <div className="mb-2">{t('EatingWayDetailModal.main_menu_book_note')}:</div>
                    <Row className="mb-2">
                      <Col sm={{ span: 4, offset: 2 }}>
                        <div
                          className="px-3 py-2 text-center"
                          style={{
                            background: `linear-gradient(to right, ${watchMainGenreBgColor1} 0%, ${watchMainGenreBgColor2} 100%)`,
                            color: watchMainGenreTextColor,
                            height: '29px',
                          }}
                        >
                          {t('EatingWayDetailModal.genre')}
                        </div>
                      </Col>
                      <Col sm={3} className="pe-1">
                        <Button className="w-100" onClick={handleColorMainGenreClick}>
                          {t('EatingWayDetailModal.change_text_color')}
                        </Button>
                        <Form.Control
                          ref={colorMainGenreInputRef}
                          type="color"
                          className="invisible"
                          style={{ height: 0, padding: 0 }}
                          value={watchMainGenreTextColor}
                          onChange={(event) => {
                            setValue('main_genre_text_color', event.target.value);
                          }}
                        />
                      </Col>
                      <Col sm={3} className="ps-1">
                        <Button
                          className="w-100"
                          onClick={handleOpenSelectBackgroundModal.bind(null, SelectBackgroundModalType.main_genre)}
                        >
                          {t('EatingWayDetailModal.change_background_color')}
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={{ span: 4, offset: 2 }}>
                        <div
                          className="px-3 py-2 text-center"
                          style={{
                            background: `linear-gradient(to right, ${watchMainSubGenreBgColor1} 0%, ${watchMainSubGenreBgColor2} 100%)`,
                            color: watchMainSubGenreTextColor,
                            height: '29px',
                          }}
                        >
                          {t('EatingWayDetailModal.subgenre')}
                        </div>
                      </Col>
                      <Col sm={3} className="pe-1">
                        <Button className="w-100" onClick={handleColorMainSubGenreClick}>
                          {t('EatingWayDetailModal.change_text_color')}
                        </Button>
                        <Form.Control
                          ref={colorMainSubGenreInputRef}
                          type="color"
                          className="invisible"
                          style={{ height: 0, padding: 0 }}
                          value={watchMainSubGenreTextColor}
                          onChange={(event) => {
                            setValue('main_subgenre_text_color', event.target.value);
                          }}
                        />
                      </Col>
                      <Col sm={3} className="ps-1">
                        <Button
                          className="w-100"
                          onClick={handleOpenSelectBackgroundModal.bind(null, SelectBackgroundModalType.main_subgenre)}
                        >
                          {t('EatingWayDetailModal.change_background_color')}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  <div>
                    <div className="mb-2">{t('EatingWayDetailModal.other_menu_book_note')}:</div>
                    <Row className="mb-2">
                      <Col sm={{ span: 4, offset: 2 }}>
                        <div
                          className="px-3 py-2 text-center"
                          style={{
                            background: `linear-gradient(to right, ${watchOtherGenreBgColor1} 0%, ${watchOtherGenreBgColor2} 100%)`,
                            color: watchOtherGenreTextColor,
                            height: '29px',
                          }}
                        >
                          {t('EatingWayDetailModal.genre')}
                        </div>
                      </Col>
                      <Col sm={3} className="pe-1">
                        <Button className="w-100" onClick={handleColorOtherGenreClick}>
                          {t('EatingWayDetailModal.change_text_color')}
                        </Button>
                        <Form.Control
                          ref={colorOtherGenreInputRef}
                          type="color"
                          className="invisible"
                          style={{ height: 0, padding: 0 }}
                          value={watchOtherGenreTextColor}
                          onChange={(event) => {
                            setValue('other_genre_text_color', event.target.value);
                          }}
                        />
                      </Col>
                      <Col sm={3} className="ps-1">
                        <Button
                          className="w-100"
                          onClick={handleOpenSelectBackgroundModal.bind(null, SelectBackgroundModalType.other_genre)}
                        >
                          {t('EatingWayDetailModal.change_background_color')}
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={{ span: 4, offset: 2 }}>
                        <div
                          className="px-3 py-2 text-center"
                          style={{
                            background: `linear-gradient(to right, ${watchOtherSubGenreBgColor1} 0%, ${watchOtherSubGenreBgColor2} 100%)`,
                            color: watchOtherSubGenreTextColor,
                            height: '29px',
                          }}
                        >
                          {t('EatingWayDetailModal.subgenre')}
                        </div>
                      </Col>
                      <Col sm={3} className="pe-1">
                        <Button className="w-100" onClick={handleColorOtherSubGenreClick}>
                          {t('EatingWayDetailModal.change_text_color')}
                        </Button>
                        <Form.Control
                          ref={colorOtherSubGenreInputRef}
                          type="color"
                          className="invisible"
                          style={{ height: 0, padding: 0 }}
                          value={watchOtherSubGenreTextColor}
                          onChange={(event) => {
                            setValue('other_subgenre_text_color', event.target.value);
                          }}
                        />
                      </Col>
                      <Col sm={3} className="ps-1">
                        <Button
                          className="w-100"
                          onClick={handleOpenSelectBackgroundModal.bind(null, SelectBackgroundModalType.other_subgenre)}
                        >
                          {t('EatingWayDetailModal.change_background_color')}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </td>
              </tr>
              <tr>
                <th rowSpan={7}>{t('EatingWayDetailModal.head_title_area_setting')}</th>
                <th>{t('EatingWayDetailModal.area_type_1')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE1)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_2')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE2)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_3')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE3)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_4')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE4)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_5')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE5)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_6')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE6)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayDetailModal.area_type_7')}</th>
                <td className="text-center">
                  <Button onClick={handleOpenEatingWayAreaImageModal.bind(null, EATING_WAY_AREA_IMAGE_TYPE.TYPE7)}>
                    {t('EatingWayDetailModal.change')}
                  </Button>
                </td>
              </tr>
              <tr>
                <th colSpan={2}>{t('EatingWayDetailModal.note')}</th>
                <td>
                  <Form.Control as="textarea" rows={3} {...register('note')}></Form.Control>
                </td>
              </tr>
            </tbody>
          </Table>
        </form>

        {showSelectBackgroundModal &&
          selectBackgroundModalData &&
          createPortal(
            <SelectBackgroundModal
              title={selectBackgroundModalData.title}
              subtitle={selectBackgroundModalData.subtitle}
              bgcolor1={selectBackgroundModalData.bgcolor1}
              bgcolor2={selectBackgroundModalData.bgcolor2}
              defaultBgColor1={selectBackgroundModalData.defaultBgColor1}
              defaultBgColor2={selectBackgroundModalData.defaultBgColor2}
              onClose={handleCloseSelectBackgroundModal}
              onReflect={handleReflectSelectBackgroundModal}
            />,
            document.body
          )}

        {showEatingWayAreaImageModal &&
          eatingWayAreaImageModalData &&
          createPortal(
            <EatingWayAreaImageModal
              formType="select"
              eatingWayAreaImage={eatingWayAreaImageModalData.eatingWayAreaImage}
              eatingWayName={eatingWayAreaImageModalData.eatingWayName}
              propertyName={eatingWayAreaImageModalData.propertyName}
              areaType={eatingWayAreaImageModalData.areaType}
              onClose={handleCloseEatingWayAreaImageModal}
              onReflect={handleReflectEatingWayAreaImageModal}
            />,
            document.body
          )}
      </Modal.Body>
    </Modal>
  );
}
